import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import ContractDetailsModal from "../../components/Modal/ContractDetailsModal";
import { apiUrl, unwrapPaginated } from "../../config/api";
import { EMPTY_VALUE, formatCurrency, formatDate } from "../../i18n/format";
import { isContractStatus } from "../../lib/contractStatus";
import { useCompaniesMyCompaniesRetrieve } from "../../services/companies/generated/companies/companies";
import type { Company } from "../../services/companies/generated/model";
import { useMatchRequestsContractsList } from "../../services/contracts/generated/contracts/contracts";
import type { Contract as ApiContract } from "../../services/contracts/generated/model";

import "./ContractsPage.css";

type StatusKey = "signed" | "pending" | "expired";

function mapStatus(apiStatus: string): StatusKey {
  const s = (apiStatus || "").toLowerCase();
  if (s === "active" || s === "accepted" || s === "completed") return "signed";
  if (s === "cancelled") return "expired";
  return "pending";
}

function formatValue(raw: string | null): string {
  if (raw == null || raw === "") return EMPTY_VALUE;
  const num = Number(raw);
  if (Number.isNaN(num)) return raw;
  if (num >= 1_000) return formatCurrency(num, "USD", { notation: "compact", maximumFractionDigits: 1 });
  return formatCurrency(num);
}

const ContractsPage = () => {
  const { t } = useTranslation("b2b");
  const contractStatusLabel = (status: string): string => {
    const s = (status || "").toLowerCase();
    return isContractStatus(s) ? t(`contracts.status.${s}`) : status;
  };
  const [selectedContract, setSelectedContract] = useState<ApiContract | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all-status");
  const [typeFilter, setTypeFilter] = useState("all-types");

  const {
    data: contractsData,
    isLoading,
    error,
  } = useMatchRequestsContractsList({
    search: search || undefined,
    status: statusFilter,
    type: typeFilter,
  });
  const { data: companiesData } = useCompaniesMyCompaniesRetrieve();

  const myCompanyId = unwrapPaginated<Company>(companiesData)[0]?.id ?? null;
  const contracts = unwrapPaginated<ApiContract>(contractsData);

  const counterpartName = (c: ApiContract): string => {
    if (myCompanyId != null && Number(c.companyAId) === myCompanyId) return c.companyBName || "—";
    return c.companyAName || "—";
  };

  const handleViewDetails = (contract: ApiContract) => {
    setSelectedContract(contract);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedContract(null);
  };

  const handleExportAll = () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    params.set("status", statusFilter);
    params.set("type", typeFilter);
    const url = apiUrl(`/api/v1/matchRequests/contracts/export/?${params.toString()}`);
    window.location.href = url;
  };

  const errorMessage = useMemo(() => (error ? t("contracts.errors.load") : null), [error, t]);

  const getStatusClass = (status: StatusKey) => {
    switch (status) {
      case "signed":
        return "contracts__status--signed";
      case "pending":
        return "contracts__status--pending";
      case "expired":
        return "contracts__status--expired";
      default:
        return "";
    }
  };

  return (
    <DashboardLayout showSearch={false}>
      <div className="contracts">
        <header className="contracts__header">
          <div>
            <h1 className="dashboard__title">{t("contracts.title")}</h1>
            <p className="dashboard__subtitle">{t("contracts.subtitle")}</p>
          </div>
          <button
            type="button"
            className="contracts__export-button"
            aria-label={t("contracts.exportAllAria")}
            onClick={handleExportAll}
            disabled={contracts.length === 0}
          >
            <i className="fa-solid fa-download" />
            <span>{t("contracts.exportAll")}</span>
          </button>
        </header>

        <div className="contracts__filters">
          <div className="contracts__search">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              type="search"
              placeholder={t("contracts.searchPlaceholder")}
              aria-label={t("contracts.searchAria")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="contracts__filter-select"
            aria-label={t("contracts.filters.statusAria")}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all-status">{t("contracts.filters.allStatuses")}</option>
            <option value="draft">{t("contracts.status.draft")}</option>
            <option value="active">{t("contracts.status.active")}</option>
            <option value="completed">{t("contracts.status.completed")}</option>
            <option value="cancelled">{t("contracts.status.cancelled")}</option>
          </select>
          <select
            className="contracts__filter-select"
            aria-label={t("contracts.filters.typeAria")}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all-types">{t("contracts.filters.allTypes")}</option>
            <option value="Partnership">{t("contracts.types.partnership")}</option>
            <option value="Technology">{t("contracts.types.technology")}</option>
            <option value="Supply">{t("contracts.types.supply")}</option>
            <option value="Distribution">{t("contracts.types.distribution")}</option>
            {/* API filter value, not UI copy. i18n-ignore-next-line */}
            <option value="Joint Venture">{t("contracts.types.jointVenture")}</option>
          </select>
        </div>

        {errorMessage && (
          <div className="contracts__error" role="alert">
            <i className="fa-solid fa-circle-exclamation" />
            <span>{errorMessage}</span>
          </div>
        )}

        {isLoading ? (
          <div className="contracts__loading">
            <i className="fa-solid fa-spinner fa-spin" />
            <span>{t("contracts.loading")}</span>
          </div>
        ) : contracts.length === 0 ? (
          <p className="contracts__empty">{t("contracts.empty")}</p>
        ) : (
          <div className="contracts__list">
            {contracts.map((contract) => {
              const statusKey = mapStatus(contract.status);
              const counterpart = counterpartName(contract);
              const title = t("contracts.cardTitle", {
                type: contract.contractType || t("contracts.contractFallback"),
                counterpart,
              });
              return (
                <article key={contract.id} className="contracts__card">
                  <div className="contracts__card-content">
                    <div className="contracts__card-icon">
                      <i className="fa-regular fa-file-lines" />
                    </div>
                    <div className="contracts__card-details">
                      <h2 className="contracts__card-title">{title}</h2>
                      <p className="contracts__card-company">{counterpart}</p>
                      <div className="contracts__card-meta">
                        <span className="contracts__meta-item">
                          <i className="fa-regular fa-calendar" />
                          {formatDate(contract.dateSigned || contract.startDate)}
                        </span>
                        <span className="contracts__meta-item">
                          {t("contracts.meta.type", { type: contract.contractType || EMPTY_VALUE })}
                        </span>
                        <span className="contracts__meta-value">
                          {t("contracts.meta.value", { value: formatValue(contract.contractValue) })}
                        </span>
                      </div>
                    </div>
                    <span className={`contracts__status ${getStatusClass(statusKey)}`}>
                      {contractStatusLabel(contract.status)}
                    </span>
                  </div>
                  <div className="contracts__card-separator" />
                  <div className="contracts__card-actions">
                    <button
                      type="button"
                      className="contracts__action-button contracts__action-button--primary"
                      aria-label={t("contracts.viewDetailsAria")}
                      onClick={() => handleViewDetails(contract)}
                    >
                      <i className="fa-regular fa-eye" />
                      <span>{t("contracts.viewDetails")}</span>
                    </button>
                    {contract.documents.length > 0 && (
                      <a
                        href={contract.documents[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contracts__action-button contracts__action-button--secondary"
                        aria-label={t("contracts.downloadAria")}
                      >
                        <i className="fa-solid fa-download" />
                        <span>{t("contracts.download")}</span>
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <ContractDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        contract={selectedContract}
        myCompanyId={myCompanyId}
      />
    </DashboardLayout>
  );
};

export default ContractsPage;
